// src/pages/Disclaimer.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

const OPERATOR = {
  platformName: "ZEDRO Academy",
  legalTradeName: "Académie ZEDRO",
};

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return ["es", "fr"].includes(savedLanguage) ? savedLanguage : "es";
}

const DISCLAIMER_COPY = {
  es: {
    title: "Descargo de responsabilidad",
    updated: "Última actualización: 17 de julio de 2026",
    intro: (
      <>
        Los contenidos, recursos y herramientas ofrecidos en{" "}
        <strong>{OPERATOR.platformName}</strong>, plataforma operada por{" "}
        <strong>{OPERATOR.legalTradeName}</strong>, tienen fines formativos,
        educativos, reflexivos e informativos.
      </>
    ),
    paragraphs: [
      <>
        Este Descargo debe leerse junto con los{" "}
        <Link to="/legal/terminos">
          Términos y Condiciones de Uso, Compra y Suscripción
        </Link>
        , la <Link to="/legal/privacidad">Política de Privacidad</Link>, la{" "}
        <Link to="/legal/cookies">Política de Cookies</Link> y el{" "}
        <Link to="/legal/aviso-legal">Aviso legal</Link>.
      </>,
    ],
    sections: [
      {
        title: "1. Contenido general, no asesoría personalizada",
        content: (
          <>
            El contenido de {OPERATOR.platformName} es de carácter general y no
            tiene en cuenta todas las circunstancias particulares de cada
            persona. No constituye asesoría médica, psicológica, psiquiátrica,
            terapéutica, legal, financiera, contable, fiscal, profesional ni
            pastoral personalizada.
          </>
        ),
        extra: (
          <>
            Cuando una situación requiera evaluación, diagnóstico, tratamiento,
            representación, planificación especializada o acompañamiento
            individual, corresponde consultar a una persona profesional
            debidamente calificada.
          </>
        ),
      },
      {
        title: "2. Decisiones y aplicación personal",
        content: (
          <>
            Cada persona es responsable de evaluar su contexto, necesidades,
            límites y circunstancias antes de aplicar una idea, ejercicio,
            reflexión o recomendación general presentada en la plataforma.
          </>
        ),
        extra: (
          <>
            El uso del contenido no crea una relación profesional, clínica,
            terapéutica, fiduciaria, jurídica ni pastoral individual entre la
            persona usuaria y {OPERATOR.legalTradeName}.
          </>
        ),
      },
      {
        title: "3. Ausencia de garantías de resultado",
        content: (
          <>
            {OPERATOR.platformName} no garantiza resultados personales,
            espirituales, emocionales, relacionales, profesionales, comerciales,
            financieros, físicos o de salud determinados.
          </>
        ),
        extra: (
          <>
            Los resultados pueden variar según las decisiones, la constancia, el
            contexto, las circunstancias externas y otros factores que no están
            bajo el control de {OPERATOR.legalTradeName}. Los testimonios,
            ejemplos o experiencias que puedan presentarse no constituyen una
            promesa de resultados equivalentes.
          </>
        ),
      },
      {
        title: "4. Contenido de inspiración cristiana",
        content: (
          <>
            Parte del contenido puede abordar principios bíblicos, fe cristiana,
            carácter, propósito, mayordomía, relaciones y vida cotidiana desde
            una perspectiva formativa.
          </>
        ),
        extra: (
          <>
            Este contenido no sustituye la libertad de conciencia, el estudio
            personal, la participación voluntaria en una comunidad de fe ni el
            acompañamiento pastoral individual cuando una persona decida
            buscarlo. Tampoco se presenta como una garantía de resultados
            espirituales, materiales o económicos.
          </>
        ),
      },
      {
        title: "5. Salud física, mental y emocional",
        content: (
          <>
            Toda referencia a hábitos, bienestar, descanso, alimentación,
            emociones, disciplina, cuerpo o vida diaria es general y educativa.
            No debe utilizarse para diagnosticar, tratar o prevenir una condición
            médica o de salud mental ni para modificar o suspender un tratamiento
            indicado por una persona profesional calificada.
          </>
        ),
        extra: (
          <>
            Las herramientas de la plataforma no están diseñadas para atender
            emergencias médicas, psicológicas o de seguridad. Ante una situación
            urgente, corresponde contactar de inmediato a los servicios de
            emergencia o de atención profesional disponibles en la localidad de
            la persona.
          </>
        ),
      },
      {
        title: "6. Funciones asistidas por inteligencia artificial",
        content: (
          <>
            Algunas funciones pueden utilizar inteligencia artificial para
            ayudar a organizar, reformular, resumir o procesar contenido
            proporcionado por la persona usuaria.
          </>
        ),
        extra: (
          <>
            Los resultados automatizados pueden contener errores, omisiones o
            interpretaciones imprecisas. Deben revisarse antes de ser utilizados
            y no sustituyen el criterio humano ni la asesoría profesional
            correspondiente. No se debe ingresar información altamente sensible
            o innecesaria en estas funciones.
          </>
        ),
      },
      {
        title: "7. Información y recursos de terceros",
        content: (
          <>
            La plataforma puede mencionar o enlazar recursos externos. La
            presencia de un enlace no constituye necesariamente una aprobación,
            garantía o recomendación integral de su contenido.
          </>
        ),
        extra: (
          <>
            Los recursos externos se rigen por sus propias condiciones y
            políticas. Esto no excluye las obligaciones que la ley pueda imponer
            a {OPERATOR.legalTradeName} respecto de los proveedores o servicios
            que utilice para operar la plataforma.
          </>
        ),
      },
      {
        title: "8. Límites legales de este Descargo",
        content: (
          <>
            En la medida permitida por la ley, {OPERATOR.legalTradeName} no
            responde por decisiones tomadas exclusivamente a partir de contenido
            general ni por pérdidas indirectas derivadas de un uso contrario a
            los documentos legales, de credenciales comprometidas por la persona
            usuaria o de acontecimientos fuera de su control razonable.
          </>
        ),
        extra: (
          <>
            Nada en este Descargo excluye o limita las obligaciones derivadas del
            hecho propio de {OPERATOR.legalTradeName} o de sus representantes,
            la responsabilidad por falta intencional o falta grave, la
            responsabilidad por perjuicio corporal o moral, ni los derechos y
            recursos de las personas consumidoras que no puedan excluirse o
            limitarse conforme a la ley aplicable.
          </>
        ),
      },
      {
        title: "9. Alcance y actualizaciones",
        content: (
          <>
            Este Descargo se aplica al contenido general, los recursos y las
            herramientas disponibles en {OPERATOR.platformName}. Las condiciones
            contractuales de una compra, membresía o servicio se encuentran en
            los{" "}
            <Link to="/legal/terminos">
              Términos y Condiciones de Uso, Compra y Suscripción
            </Link>
            .
          </>
        ),
        extra: (
          <>
            El documento puede actualizarse para reflejar cambios legales,
            operativos o en los servicios ofrecidos. La versión vigente es la
            publicada en el sitio con su fecha de actualización.
          </>
        ),
      },
    ],
  },

  fr: {
    title: "Avis de non-responsabilité",
    updated: "Dernière mise à jour : 17 juillet 2026",
    intro: (
      <>
        Les contenus, ressources et outils offerts dans{" "}
        <strong>{OPERATOR.platformName}</strong>, une plateforme exploitée par{" "}
        <strong>{OPERATOR.legalTradeName}</strong>, sont fournis à des fins de
        formation, d’éducation, de réflexion et d’information.
      </>
    ),
    paragraphs: [
      <>
        Le présent Avis doit être lu avec les{" "}
        <Link to="/legal/terminos">
          Conditions d’utilisation, d’achat et d’abonnement
        </Link>
        , la <Link to="/legal/privacidad">Politique de confidentialité</Link>, la{" "}
        <Link to="/legal/cookies">Politique de cookies</Link> et l’{" "}
        <Link to="/legal/aviso-legal">Avis juridique</Link>.
      </>,
    ],
    sections: [
      {
        title: "1. Contenu général, non personnalisé",
        content: (
          <>
            Le contenu de {OPERATOR.platformName} est de nature générale et ne
            tient pas compte de toutes les circonstances propres à chaque
            personne. Il ne constitue pas un avis médical, psychologique,
            psychiatrique, thérapeutique, juridique, financier, comptable,
            fiscal, professionnel ou pastoral personnalisé.
          </>
        ),
        extra: (
          <>
            Lorsqu’une situation exige une évaluation, un diagnostic, un
            traitement, une représentation, une planification spécialisée ou un
            accompagnement individuel, il convient de consulter une personne
            professionnelle dûment qualifiée.
          </>
        ),
      },
      {
        title: "2. Décisions et mise en pratique personnelle",
        content: (
          <>
            Chaque personne est responsable d’évaluer son contexte, ses besoins,
            ses limites et sa situation avant d’appliquer une idée, un exercice,
            une réflexion ou une recommandation générale présentée dans la
            plateforme.
          </>
        ),
        extra: (
          <>
            L’utilisation du contenu ne crée aucune relation professionnelle,
            clinique, thérapeutique, fiduciaire, juridique ou pastorale
            individuelle entre la personne utilisatrice et{" "}
            {OPERATOR.legalTradeName}.
          </>
        ),
      },
      {
        title: "3. Absence de garantie de résultat",
        content: (
          <>
            {OPERATOR.platformName} ne garantit aucun résultat personnel,
            spirituel, émotionnel, relationnel, professionnel, commercial,
            financier, physique ou lié à la santé.
          </>
        ),
        extra: (
          <>
            Les résultats peuvent varier selon les décisions, la constance, le
            contexte, les circonstances externes et d’autres facteurs qui ne
            relèvent pas du contrôle d’{OPERATOR.legalTradeName}. Les
            témoignages, exemples ou expériences qui peuvent être présentés ne
            constituent pas une promesse de résultats équivalents.
          </>
        ),
      },
      {
        title: "4. Contenu d’inspiration chrétienne",
        content: (
          <>
            Une partie du contenu peut aborder des principes bibliques, la foi
            chrétienne, le caractère, le sens, l’intendance, les relations et la
            vie quotidienne dans une perspective de formation.
          </>
        ),
        extra: (
          <>
            Ce contenu ne remplace pas la liberté de conscience, l’étude
            personnelle, la participation volontaire à une communauté de foi ni
            l’accompagnement pastoral individuel qu’une personne peut choisir de
            rechercher. Il n’est pas non plus présenté comme une garantie de
            résultats spirituels, matériels ou économiques.
          </>
        ),
      },
      {
        title: "5. Santé physique, mentale et émotionnelle",
        content: (
          <>
            Toute référence aux habitudes, au bien-être, au repos, à
            l’alimentation, aux émotions, à la discipline, au corps ou à la vie
            quotidienne est générale et éducative. Elle ne doit pas servir à
            diagnostiquer, traiter ou prévenir un problème médical ou de santé
            mentale ni à modifier ou interrompre un traitement recommandé par
            une personne professionnelle qualifiée.
          </>
        ),
        extra: (
          <>
            Les outils de la plateforme ne sont pas conçus pour répondre à une
            urgence médicale, psychologique ou de sécurité. En situation
            urgente, il convient de communiquer immédiatement avec les services
            d’urgence ou les services professionnels accessibles dans la
            localité de la personne.
          </>
        ),
      },
      {
        title: "6. Fonctions assistées par intelligence artificielle",
        content: (
          <>
            Certaines fonctions peuvent utiliser l’intelligence artificielle
            afin d’aider à organiser, reformuler, résumer ou traiter du contenu
            fourni par la personne utilisatrice.
          </>
        ),
        extra: (
          <>
            Les résultats automatisés peuvent comporter des erreurs, des
            omissions ou des interprétations inexactes. Ils doivent être vérifiés
            avant d’être utilisés et ne remplacent ni le jugement humain ni
            l’avis professionnel approprié. Aucun renseignement hautement
            sensible ou inutile ne devrait être saisi dans ces fonctions.
          </>
        ),
      },
      {
        title: "7. Renseignements et ressources de tiers",
        content: (
          <>
            La plateforme peut mentionner des ressources externes ou contenir
            des liens vers celles-ci. La présence d’un lien ne constitue pas
            nécessairement une approbation, une garantie ou une recommandation
            complète de son contenu.
          </>
        ),
        extra: (
          <>
            Les ressources externes sont régies par leurs propres conditions et
            politiques. Cela n’exclut pas les obligations que la loi peut imposer
            à {OPERATOR.legalTradeName} relativement aux fournisseurs ou services
            qu’elle utilise pour exploiter la plateforme.
          </>
        ),
      },
      {
        title: "8. Limites juridiques du présent Avis",
        content: (
          <>
            Dans la mesure permise par la loi,{" "}
            {OPERATOR.legalTradeName} n’est pas responsable des décisions prises
            exclusivement à partir d’un contenu général ni des pertes indirectes
            résultant d’une utilisation contraire aux documents juridiques,
            d’identifiants compromis par la personne utilisatrice ou
            d’événements hors de son contrôle raisonnable.
          </>
        ),
        extra: (
          <>
            Rien dans le présent Avis n’exclut ni ne limite les obligations
            découlant du fait personnel d’{OPERATOR.legalTradeName} ou de ses
            représentants, la responsabilité pour faute intentionnelle ou faute
            lourde, la responsabilité pour préjudice corporel ou moral, ni les
            droits et recours des consommateurs qui ne peuvent être exclus ou
            limités par la loi applicable.
          </>
        ),
      },
      {
        title: "9. Portée et mises à jour",
        content: (
          <>
            Le présent Avis s’applique au contenu général, aux ressources et aux
            outils disponibles dans {OPERATOR.platformName}. Les conditions
            contractuelles propres à un achat, à un abonnement ou à un service
            se trouvent dans les{" "}
            <Link to="/legal/terminos">
              Conditions d’utilisation, d’achat et d’abonnement
            </Link>
            .
          </>
        ),
        extra: (
          <>
            Le document peut être mis à jour afin de refléter des changements
            juridiques, opérationnels ou apportés aux services offerts. La
            version en vigueur est celle publiée sur le site avec sa date de mise
            à jour.
          </>
        ),
      },
    ],
  },
};

export default function Disclaimer() {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function syncLanguage(nextLanguage) {
      if (["es", "fr"].includes(nextLanguage)) {
        setLanguage(nextLanguage);
      } else {
        setLanguage("es");
      }
    }

    function handleLanguageEvent(event) {
      syncLanguage(event?.detail?.language);
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        syncLanguage(event.newValue);
      }
    }

    syncLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
    window.addEventListener("zedro:languagechange", handleLanguageEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("zedro:languagechange", handleLanguageEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const copy = DISCLAIMER_COPY[language] || DISCLAIMER_COPY.es;

  return (
    <main
      style={{
        maxWidth: 900,
        margin: "2rem auto",
        padding: "0 1rem",
        fontFamily: "Questrial, sans-serif",
        lineHeight: 1.75,
        color: "#2c3b51",
      }}
    >
      <h1>{copy.title}</h1>

      <p
        style={{
          color: "rgba(44, 59, 81, 0.72)",
          fontSize: "0.95rem",
          marginTop: "-0.4rem",
        }}
      >
        {copy.updated}
      </p>

      <p>{copy.intro}</p>

      {copy.paragraphs.map((paragraph, index) => (
        <p key={`paragraph-${index}`}>{paragraph}</p>
      ))}

      {copy.sections.map((section) => (
        <section key={section.title} style={{ marginTop: "1.5rem" }}>
          <h2
            style={{
              fontSize: "1.2rem",
              marginBottom: "0.45rem",
              color: "#102b43",
            }}
          >
            {section.title}
          </h2>

          <p>{section.content}</p>
          {section.extra && <p>{section.extra}</p>}
        </section>
      ))}
    </main>
  );
}
